import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "@/assets/css/AGGrid-custom.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  type CellClassParams,
  type ColDef,
} from "ag-grid-community";
import { useDealsProvider } from "@/context/DealsContext";
import { useReferenceLists } from "@/context/GlobalGetLists";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import type { Deal } from "@/types/deal";
import { ErrorDialog } from "@/components/common/errorDialog";
import { RightSideBar } from "@/components/layout/RightSideBar";
import { DealSidePanel } from "@/components/deal/DealSidePanel";
import { useGlobalProvider } from "@/context/GlobalContext";
import { useTenantFeatures } from "@/context/TenantFeaturesContext";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { UserAllocationsTally } from "@/pages/pipeline/UserAllocationsTally";
import { usePipelineFilters } from "./usePipelineFilters";
import type { PipelineViewId } from "./pipelineViews";
import { usePipelineGrid } from "./usePipelineGrid";
import { usePipelineDeals } from "./usePipelineDeals";
import { usePipelineLiveUpdates } from "./usePipelineLiveUpdates";
import { usePipelineCellSave } from "./usePipelineCellSave";
import { PipelineToolbar } from "./PipelineToolbar";
import { PipelineTermSheetDialog } from "./PipelineTermSheetDialog";
import { KillReasonDialog } from "@/components/deal/KillReasonDialog";
import { PipelineConflictNotice } from "./PipelineConflictNotice";
import { PipelineFeeReminder } from "./PipelineFeeReminder";
import { StatusBandContent, StatusBandRow } from "./components/StatusBandRow";
import {
  isStaleRow,
  isStatusBandRow,
  withStatusBands,
  type StatusBandContext,
  type StatusBandRow as StatusBand,
} from "./pipelineMeeting";

ModuleRegistry.registerModules([AllCommunityModule]);

const Pipeline = () => {
  const { canEdit, user } = useCurrentUser();
  const { isFeatureEnabled } = useTenantFeatures();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const { setRightSideBarOpen } = useGlobalProvider();
  const {
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    killReasons,
    capitalSources,
  } = useReferenceLists();

  const { deals, initialDeals, setInitialDeals, setDeals } = useDealsProvider();

  const {
    view,
    setView,
    customColumns,
    setCustomColumns,
    editing,
    setEditing,
    myDealsOnly,
    setMyDealsOnly,
    bandOverrides,
    setBandOverrides,
    filter,
    setFilter,
    activeOnly,
    setActiveOnly,
    statusFilter,
    setStatusFilter,
    countryFilterIds,
    setCountryFilterIds,
    sectorFilterIds,
    setSectorFilterIds,
    capitalSourceFilterIds,
    setCapitalSourceFilterIds,
    filteredRows,
  } = usePipelineFilters(deals, user?.id);

  const pipelineLists = useMemo(
    () => ({
      condensedSponsors,
      sources,
      countries,
      sectors,
      status,
      users,
      loanTypes,
      riskClasses,
      securityTypes,
      transactionTypes,
      currencies,
      killReasons,
      capitalSources,
    }),
    [
      condensedSponsors,
      sources,
      countries,
      sectors,
      status,
      users,
      loanTypes,
      riskClasses,
      securityTypes,
      transactionTypes,
      currencies,
      killReasons,
      capitalSources,
    ],
  );

  const gridEditable = canEdit && editing;
  // The Meeting tab is meeting mode: status bands, changes, stale markers.
  const meetingMode = view === "meeting";
  const {
    gridRef,
    selectedDealId,
    baseColumnDefs,
    columnDefs,
    rowHeight,
    onRowClicked,
    onCellFocused,
    getRowClass,
  } = usePipelineGrid({
    editable: gridEditable,
    view,
    customColumns,
    lists: pipelineLists,
    setRightSideBarOpen,
  });

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) api.resetRowHeights();
  }, [gridRef, rowHeight]);

  const { getAllDeals } = usePipelineDeals({
    setDeals,
    setInitialDeals,
    activeOnly,
    setActiveOnly,
    showErrorFromResponse,
  });

  const { isLive, refresh, noteOwnSave, liveCellClassRules, liveTooltip } =
    usePipelineLiveUpdates({
      activeOnly,
      // Cell saves run alongside refreshes: in-flight values are kept by the merge.
      paused: false,
      gridRef,
      deals,
      initialDeals,
      setDeals,
      setInitialDeals,
    });

  const {
    onCellValueChanged,
    cellClassRules,
    isSaving,
    conflict,
    applyMine,
    dismissConflict,
    termSheetDialog,
    closeTermSheetDialog,
    feeReminder,
    dismissFeeReminder,
    killRequest,
    confirmKill,
    cancelKill,
  } = usePipelineCellSave({
    gridRef,
    initialDeals,
    setDeals,
    setInitialDeals,
    showErrorFromResponse,
    onSaved: noteOwnSave,
    refresh,
    advisorFeesEnabled: isFeatureEnabled("deal_advisor_fees"),
  });

  // Read by the class rules when AG Grid evaluates them.
  const meetingRef = useRef({ meetingMode });
  useEffect(() => {
    meetingRef.current = { meetingMode };
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) {
      // Cell classes only: redrawing rows would close an open editor.
      api.refreshCells({ force: true });
    }
  }, [meetingMode, gridRef]);

  const defaultColDef = useMemo<ColDef<Deal>>(
    () => ({
      tooltipValueGetter: liveTooltip,
      cellClassRules: {
        ...cellClassRules,
        ...liveCellClassRules,
        "pipeline-cell--stale": (params: CellClassParams<Deal>) =>
          meetingRef.current.meetingMode &&
          params.column.getColId() === "deal_name" &&
          !!params.data &&
          isStaleRow(params.data),
      },
    }),
    [cellClassRules, liveCellClassRules, liveTooltip],
  );

  // Meeting mode heads each status group with a band row (count and GBP total).
  const gridRows = useMemo(
    () =>
      (meetingMode
        ? withStatusBands(filteredRows, bandOverrides)
        : filteredRows) as unknown as Deal[],
    [filteredRows, meetingMode, bandOverrides],
  );

  // Opening the Meeting tab starts from the default groups (Closed ones
  // collapsed, the rest open), whatever was toggled earlier.
  const selectView = useCallback(
    (next: PipelineViewId) => {
      if (next === "meeting") setBandOverrides({});
      setView(next);
    },
    [setBandOverrides, setView],
  );

  const toggleBand = useCallback(
    (bandId: string) => {
      const band = (gridRows as unknown[]).find(
        (row): row is StatusBand => isStatusBandRow(row) && row.id === bandId,
      );
      if (band)
        setBandOverrides({ ...bandOverrides, [bandId]: !band.collapsed });
    },
    [bandOverrides, gridRows, setBandOverrides],
  );
  const gridContext = useMemo<StatusBandContext>(
    () => ({ toggleBand }),
    [toggleBand],
  );

  // The band of the group at the top of the viewport stays pinned under the
  // column headers (AG Grid's own sticky groups are Enterprise-only).
  const [stickyBand, setStickyBand] = useState<StatusBand | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const stickyFrameRef = useRef<number | null>(null);
  const updateStickyBand = useCallback(() => {
    if (stickyFrameRef.current != null) return;
    stickyFrameRef.current = requestAnimationFrame(() => {
      stickyFrameRef.current = null;
      const api = gridRef.current?.api;
      if (!api || api.isDestroyed() || !meetingRef.current.meetingMode) {
        setStickyBand(null);
        return;
      }
      const top = api.getVerticalPixelRange().top;
      let current: { data: StatusBand; rowTop: number } | null = null;
      const count = api.getDisplayedRowCount();
      for (let i = 0; i < count; i++) {
        const node = api.getDisplayedRowAtIndex(i);
        if (!node || node.rowTop == null) continue;
        if (node.rowTop > top) break;
        if (isStatusBandRow(node.data)) {
          current = { data: node.data, rowTop: node.rowTop };
        }
      }
      // Not needed while the band itself is still at the top of the grid.
      setStickyBand(current && top - current.rowTop > 1 ? current.data : null);
      const header = document.querySelector<HTMLElement>(
        ".pipeline-deals-grid .ag-header",
      );
      setHeaderHeight(header?.offsetHeight ?? 0);
    });
  }, [gridRef]);
  useEffect(() => {
    updateStickyBand();
  }, [meetingMode, gridRows, updateStickyBand]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PipelineToolbar
          filter={filter}
          setFilter={setFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusOptions={status}
          countryFilterIds={countryFilterIds}
          setCountryFilterIds={setCountryFilterIds}
          countries={countries}
          sectorFilterIds={sectorFilterIds}
          setSectorFilterIds={setSectorFilterIds}
          sectors={sectors}
          capitalSourceFilterIds={capitalSourceFilterIds}
          setCapitalSourceFilterIds={setCapitalSourceFilterIds}
          capitalSources={capitalSources}
          isLive={isLive}
          isSaving={isSaving}
          view={view}
          setView={selectView}
          customColumns={customColumns}
          setCustomColumns={setCustomColumns}
          allColumns={baseColumnDefs}
          activeOnly={activeOnly}
          getAllDeals={getAllDeals}
          canEdit={canEdit}
          editing={editing}
          setEditing={setEditing}
          myDealsOnly={myDealsOnly}
          setMyDealsOnly={setMyDealsOnly}
          gridRef={gridRef}
          isFeatureEnabled={isFeatureEnabled}
          showErrorFromResponse={showErrorFromResponse}
        />
      </Box>

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            p: 0,
            minWidth: 0,
            minHeight: 0,
            position: "relative",
          }}
        >
          <div
            className={[
              "ag-theme-alpine pipeline-deals-grid",
              rowHeight > 27 && "pipeline-deals-grid--tall",
              gridEditable && "pipeline-deals-grid--editing",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ width: "100%", height: "100%", position: "relative" }}
          >
            {meetingMode && stickyBand && (
              <div
                className="pipeline-sticky-band"
                style={{ top: headerHeight, right: 16 }}
              >
                <StatusBandContent
                  band={stickyBand}
                  onToggle={() => toggleBand(stickyBand.id)}
                />
              </div>
            )}
            <AgGridReact<Deal>
              ref={gridRef}
              rowData={gridRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onCellValueChanged={onCellValueChanged}
              rowHeight={rowHeight}
              getRowHeight={({ data }) =>
                isStatusBandRow(data) ? 30 : rowHeight
              }
              isFullWidthRow={({ rowNode }) => isStatusBandRow(rowNode.data)}
              fullWidthCellRenderer={StatusBandRow}
              context={gridContext}
              onBodyScroll={updateStickyBand}
              onModelUpdated={updateStickyBand}
              tooltipShowDelay={300}
              onRowClicked={onRowClicked}
              onCellFocused={onCellFocused}
              getRowClass={getRowClass}
              // Double-click / Enter to edit, so clicking around a shared
              // screen doesn't open editors (each edit saves immediately).
              singleClickEdit={false}
              stopEditingWhenCellsLoseFocus={true}
              getRowId={({ data }) => String(data.id)}
            />
          </div>
          {!meetingMode && <UserAllocationsTally />}
        </Box>
        <RightSideBar>
          <DealSidePanel
            dealId={selectedDealId}
            showPhotos
            initialTab="photos"
            dealName={
              selectedDealId
                ? deals.find((d) => d.id === selectedDealId)?.deal_name
                : null
            }
            onClose={() => setRightSideBarOpen(false)}
          />
        </RightSideBar>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />

      <PipelineTermSheetDialog
        dialog={termSheetDialog}
        onClose={closeTermSheetDialog}
      />

      <KillReasonDialog
        open={killRequest !== null}
        dealName={killRequest?.dealName ?? ""}
        onCancel={cancelKill}
        onConfirm={confirmKill}
      />

      <PipelineConflictNotice
        conflict={conflict}
        onApplyMine={applyMine}
        onDismiss={dismissConflict}
      />

      <PipelineFeeReminder
        reminder={conflict ? null : feeReminder}
        onDismiss={dismissFeeReminder}
      />
    </Box>
  );
};

export default Pipeline;
